import { useContext, useRef } from "react";

// helpers
import { ApiContext, CheckoutActionTypes, CheckoutContext, PublicApiContext } from "@swiggy-private/checkout-core";
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import { PollingEnabledPaymentFlow } from "@payment-client/includes/PaymentFlow";

interface UsePennyTxnStatusPollingProps {
    pollingDurationInMs: number;
    pollingIntervalInMs: number;
    startTime?: number;
    paymentCode: string;
}

const usePennyTxnStatusPolling = ({
    pollingDurationInMs,
    pollingIntervalInMs,
    startTime,
    paymentCode,
}: UsePennyTxnStatusPollingProps) => {
    const isMountedRef = useMountedRef();
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const apiContextValue = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const txnStatusRef = useRef(false);
    const paymentFlowRef = useRef<PollingEnabledPaymentFlow | null>(null);

    useEffectOnce(() => {
        if (checkoutState.type !== CheckoutActionTypes.PENNY_TXN_STATUS_POLLER || apiContextValue.host.isNone()) {
            return;
        }

        const paymentFlow = new PollingEnabledPaymentFlow(
            apiContextValue.host.unwrap(),
            null, // order data is not required for penny txn status polling
            publicApiConfig || {},
            checkoutState.payload.transaction,
        );
        paymentFlowRef.current = paymentFlow;

        const moveToNextState = (): void => {
            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_TRANSACTION,
                payload: {
                    orderId: checkoutState.payload.orderId,
                    transaction: checkoutState.payload.transaction,
                    transactionReqData: checkoutState.payload.transactionReqData,
                    useJuspaySDK: checkoutState.payload.useJuspaySDK,
                },
            });
        };

        paymentFlow
            .checkTxnStatus({
                duration: pollingDurationInMs,
                delay: pollingIntervalInMs,
                startTime: startTime || Date.now(),
                paymentCode: paymentCode,
            })
            .then((_result) => {
                if (!isMountedRef.current || !paymentFlow.isRunning()) {
                    return;
                }

                txnStatusRef.current = true;

                if (document.visibilityState == "hidden" || document.hidden) {
                    paymentFlow.confirmPennyTransaction();
                    window.requestAnimationFrame(moveToNextState);
                } else {
                    paymentFlow.stop();
                    moveToNextState();
                }
            });

        return (): void => {
            if (!txnStatusRef.current) {
                paymentFlow.stop();
            }
        };
    });

    return (): void => paymentFlowRef.current?.stop();
};

export default usePennyTxnStatusPolling;
